import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { IneligibleSkus, MetricData, IneligibleCategoryGridProps } from './types';
import { Tooltip } from '@mui/material';
import { NewBeaconExportIcon } from 'src/components/SvgIcons';
import { withBus } from 'react-bus';
import { getEntityDisplayNames, downloadFile } from './utils';
import moment from 'moment';
import { EventBus } from 'src/types/utils';
import { IneligibleCategoryGrid, RadioOptions } from './IneligibleCategoryGrid';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { fetchMetrics } from './IneligibleEntitiesData';
import _get from 'lodash/get';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { fetchIneligibleCountForEntity } from 'src/components/AdManager/utils';
import {
  IneligibleRetailerSkuDefinition,
  IneligibleProductROASDefinition,
  IneligibleProductColumnDefinition,
  IneligibleProductAdClicksDefinition,
  IneligibleProductAdSpendDefinition,
  IneligibleProductStatusDefinition,
  IneligibleProductAdSalesDefinition
} from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import EntityTableHeader from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';

const IneligibleEntitiesGrid = ({ eventBus }: { eventBus: EventBus }) => {
  const [data, setData] = useState([]);
  const [categoryData, setCategoryData] = useState<IneligibleCategoryGridProps>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState<RadioOptions>(RadioOptions.allIneligible);
  const { mainEntity } = useAppSelector((state) => state.entityService);
  const displayName = getEntityDisplayNames(mainEntity.type);

  const { app } = useAppSelector((state) => state);

  const mergeMetricsWithStatus = ({
    metrics,
    status
  }: {
    status: IneligibleSkus[];
    metrics: MetricData;
  }): [MetricData, IneligibleCategoryGridProps] => {
    const skuToStatus = {};
    const inelgibleCategoryData = {
      adPolicingSuspended: { productCount: 0, adSpend: 0, adSales: 0, roas: 0 },
      missingDecoration: { productCount: 0, adSpend: 0, adSales: 0, roas: 0 },
      otherIneligible: { productCount: 0, adSpend: 0, adSales: 0, roas: 0 },
      allIneligible: { productCount: 0, adSpend: 0, adSales: 0, roas: 0 }
    } as IneligibleCategoryGridProps;

    status.forEach((statusItem) => {
      // eslint-disable-next-line no-unused-expressions
      statusItem[1]?.stacklineSkus.forEach((sku) => {
        const [firstStatus] = statusItem;
        skuToStatus[sku.toLowerCase()] = firstStatus;
      });
    });

    if (metrics[0]?.aggregations?.by_stacklineSku) {
      metrics[0].aggregations.by_stacklineSku.forEach((metric) => {
        const sku = _get(metric, 'additionalMetaData.stacklineSku').toLowerCase();
        metric.additionalValues.statusReason = skuToStatus[sku];
        const columnSet = new Set(['adPolicingSuspended', 'missingDecoration']);
        const key = columnSet.has(skuToStatus[sku]) ? skuToStatus[sku] : 'otherIneligible';

        inelgibleCategoryData[key].productCount += 1;
        inelgibleCategoryData[key].adSpend += metric.additionalValues.spend_sum_value;
        inelgibleCategoryData[key].adSales += metric.additionalValues.sales_sum_value;
        inelgibleCategoryData[key].roas += metric.additionalValues.returnOnAdSpend_computed_value;

        inelgibleCategoryData.allIneligible.productCount += 1;
        inelgibleCategoryData.allIneligible.adSpend += metric.additionalValues.spend_sum_value;
        inelgibleCategoryData.allIneligible.adSales += metric.additionalValues.sales_sum_value;
        inelgibleCategoryData.allIneligible.roas += metric.additionalValues.returnOnAdSpend_computed_value;
      });
    }

    return [metrics, inelgibleCategoryData];
  };
  const retailer = useAppSelector((state) => state.retailer);

  useEffect(() => {
    eventBus.emit('INELIGIBLE_CAMPAIGN_FILTER_EVENT', {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await fetchIneligibleCountForEntity(mainEntity);
        const { adPolicingSuspended, ineligible, missingDecoration, missingImage, notBuyable, missingBuyBox } = result;

        // last 30 days only
        const innerEndWeek = moment().format('YYYYww');
        const innerStartWeek = moment().subtract(30, 'days').format('YYYYww');

        const allIneligible = [
          ['adPolicingSuspended', adPolicingSuspended],
          ['missingDecoration', missingDecoration],
          // others
          ['ineligible', ineligible],
          ['missingImage', missingImage],
          ['notBuyable', notBuyable],
          ['missingBuyBox', missingBuyBox]
        ] as IneligibleSkus[];

        const uniqueSku = allIneligible.reduce((acc, x) => {
          if (x[1]?.stacklineSkus) {
            x[1].stacklineSkus.forEach((sku: string) => acc.add(sku));
          }
          return acc;
        }, new Set());

        const metrics = await fetchMetrics({
          retailer,
          mainTimePeriod: { startWeek: innerStartWeek, endWeek: innerEndWeek },
          comparisonTimePeriod: { startWeek: innerStartWeek, endWeek: innerEndWeek },
          skus: Array.from(uniqueSku)
        });

        const mergedMetrics = mergeMetricsWithStatus({ metrics, status: allIneligible });
        const newMetrics = mergedMetrics[0];
        const newCategoryData = mergedMetrics[1];

        setCategoryData(newCategoryData);
        setData(newMetrics[0].aggregations.by_stacklineSku);
        setError(null);
      } catch (err) {
        setError('Failed to fetch ineligible entities data');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [mainEntity, app, retailer]);

  const columnDefs = [
    IneligibleProductColumnDefinition,
    IneligibleRetailerSkuDefinition,
    IneligibleProductStatusDefinition,
    IneligibleProductAdClicksDefinition,
    IneligibleProductAdSpendDefinition,
    IneligibleProductAdSalesDefinition,
    IneligibleProductROASDefinition
  ];

  const filterData = useCallback(
    (dataFilter: RadioOptions): any[] => {
      switch (dataFilter) {
        case RadioOptions.adPolicingSuspended:
          return data.filter((x) => x.additionalValues.statusReason === 'adPolicingSuspended');
        case RadioOptions.missingDecoration:
          return data.filter((x) => x.additionalValues.statusReason === 'missingDecoration');
        case RadioOptions.otherIneligible:
          return data.filter(
            (x) =>
              x.additionalValues.statusReason !== 'adPolicingSuspended' &&
              x.additionalValues.statusReason !== 'missingDecoration'
          );
        case RadioOptions.allIneligible:
          return data;
        default:
          return data;
      }
    },
    [data]
  );

  const filterDataMemo = useMemo(() => filterData(filter), [filterData, filter]);
  const { showSnackbar } = useSnackbar();

  return (
    <div
      style={{
        width: '100%'
      }}
    >
      <Text variant="h4">Ineligible Products</Text>
      <BeaconSubtitleContainer>
        <Text variant="body1">
          The table displays the number of products that are ineligible for each reason type and the associated
          advertising performance metrics in this {displayName} over the last 30 days.
        </Text>
      </BeaconSubtitleContainer>
      <IneligibleCategoryGrid filter={filter} setFilter={setFilter} loading={!categoryData} {...categoryData} />
      <EntityTableHeader
        enableSwitchingLayouts
        defaultView="table"
        iconsList={[]}
        paddingTop="0px"
        paddingBottom="0px"
        title="Product Details"
        subTitle={() => {
          return (
            <BeaconSubtitleContainer>
              <Text variant="body1">
                The table displays the ineligible products and their associated advertising performance metrics in this{' '}
                {displayName} over the last 30 days.
              </Text>
              {filterDataMemo.length > 0 && (
                <Tooltip title="Export" placement="top">
                  <span
                    style={{
                      width: '24px',
                      height: '24px',
                      cursor: 'pointer',
                      marginLeft: 'auto',
                      marginBottom: 'auto'
                    }}
                  >
                    <NewBeaconExportIcon
                      width="24px"
                      height="24px"
                      id="export"
                      onClick={() => {
                        downloadFile(filterDataMemo, 'ineligible-products')
                          .then(() => {
                            showSnackbar({ type: 'success', message: 'Your download is completed.' });
                          })
                          .catch(() => {
                            showSnackbar({ type: 'error', message: 'Your download failed.' });
                          });
                      }}
                    />
                  </span>
                </Tooltip>
              )}
            </BeaconSubtitleContainer>
          );
        }}
      />

      <EntityTable
        rowData={error ? [] : filterDataMemo}
        columnDefs={columnDefs}
        shouldModifyColumnDefs={false}
        isLoading={loading}
      />
    </div>
  );
};

export default withBus('eventBus')(IneligibleEntitiesGrid);
